import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { fmtL, fmtDate, apiErr } from "@/lib/format";
import { useLiveSearch, SearchBox } from "@/components/LiveSearch";
import DocumentoPdfModal from "@/components/DocumentoPdfModal";
import { descargarArchivo } from "@/components/NotaDescarga";
import { CalculatorIcon, FilePdfIcon, DownloadSimpleIcon, TrashIcon, InfoIcon, ArrowLeftIcon } from "@phosphor-icons/react";

/**
 * Cargos Moratorios — herramienta informativa de apoyo a la cobranza.
 * Calcula la mora de las facturas vencidas de un cliente (tasa mensual proporcional por día, base 30) y genera el
 * reporte en pantalla y en PDF. No registra nada: no modifica facturas, pagos, saldos ni contabilidad, y el reporte
 * no se guarda (al descartarlo desaparece).
 */
export default function CargosMoratoriosTab() {
  const [lista, setLista] = useState(null);
  const [error, setError] = useState("");
  const [rep, setRep] = useState(null);
  const [generando, setGenerando] = useState("");
  const [preview, setPreview] = useState(false);
  const busqueda = useLiveSearch(lista?.clientes || [], (c) => [c.nombre, c.codigo_cliente, c.rtn]);

  useEffect(() => {
    api.get("/cobranza/cargos-moratorios/clientes").then(({ data }) => setLista(data)).catch((e) => setError(apiErr(e)));
  }, []);

  const generar = async (c) => {
    if (generando) return;
    setGenerando(c.id);
    try { const { data } = await api.get(`/cobranza/cargos-moratorios/${c.id}`); setRep(data); }
    catch (e) { toast.error(apiErr(e)); } finally { setGenerando(""); }
  };
  const descargar = async () => {
    try {
      await descargarArchivo(`/cobranza/cargos-moratorios/${rep.cliente.id}/pdf`, `cargos-moratorios-${rep.cliente.codigo_cliente || "cliente"}-${rep.fecha}.pdf`);
      toast.success("Reporte descargado");
    } catch (e) { toast.error(apiErr(e)); }
  };
  const descartar = () => { setPreview(false); setRep(null); };

  if (error) return <div className="text-sm text-red-300">{error}</div>;

  if (rep) {
    const t = rep.totales;
    return (
      <div className="space-y-4" data-testid="moratorios-reporte">
        <div className="flex flex-wrap items-center gap-2">
          <button type="button" onClick={descartar} className="min-h-[44px] text-sm text-text-secondary hover:text-text-primary flex items-center gap-1.5 mr-auto">
            <ArrowLeftIcon size={16} /> Volver a clientes</button>
          <button type="button" onClick={() => setPreview(true)} data-testid="moratorios-vista-previa"
            className="min-h-[44px] px-4 border border-border rounded-md text-sm flex items-center gap-2"><FilePdfIcon size={16} /> Vista previa PDF</button>
          <button type="button" onClick={descargar} data-testid="moratorios-descargar"
            className="min-h-[44px] px-4 bg-agro-green hover:bg-agro-green-hover text-black font-semibold rounded-md text-sm flex items-center gap-2"><DownloadSimpleIcon size={16} weight="bold" /> Descargar PDF</button>
          <button type="button" onClick={descartar} data-testid="moratorios-descartar"
            className="min-h-[44px] px-4 border border-border rounded-md text-sm flex items-center gap-2 hover:border-red-400/60 hover:text-red-300"><TrashIcon size={16} /> Descartar</button>
        </div>

        <section className="bg-card border border-border/60 rounded-xl p-4 sm:p-5">
          <div className="flex flex-wrap justify-between gap-3 border-b border-border/60 pb-3 mb-3">
            <div>
              <h3 className="font-heading text-lg font-semibold">REPORTE DE CARGOS MORATORIOS</h3>
              <p className="text-xs text-text-muted">Gestión de cobranza · documento informativo</p>
            </div>
            <dl className="text-xs grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5">
              <dt className="text-text-muted">Cliente</dt><dd className="font-semibold" data-testid="moratorios-cliente">{rep.cliente.nombre}</dd>
              <dt className="text-text-muted">Código</dt><dd>{rep.cliente.codigo_cliente || "—"}</dd>
              <dt className="text-text-muted">Fecha de cálculo</dt><dd>{fmtDate(rep.fecha)}</dd>
              <dt className="text-text-muted">Generado por</dt><dd>{rep.usuario}</dd>
            </dl>
          </div>
          <div className="text-xs text-text-secondary space-y-1.5 mb-3" data-testid="moratorios-intro">
            <p className="font-semibold text-text-primary">Estimado cliente:</p>
            <p>El presente reporte tiene como finalidad facilitar la revisión y conciliación de las obligaciones que actualmente presentan vencimiento. Agradecemos la atención que pueda brindar a esta información y reiteramos la importancia de regularizar la cuenta a la brevedad posible.</p>
            <p>Conforme a las condiciones establecidas en nuestras facturas, las cuentas vencidas están sujetas a un cargo moratorio mensual del {rep.tasa_mensual}%. No obstante, nuestro interés principal es procurar una solución favorable y mantener una relación comercial saludable. Por ello, de acuerdo con la regularización de la cuenta y la colaboración demostrada en el proceso de pago, los cargos moratorios presentados podrán ser considerados dentro de la gestión y conciliación correspondiente.</p>
            <p>Agradecemos su pronta atención y disposición para poner la cuenta al día.</p>
          </div>
          <div className="bg-app/60 border border-border rounded-lg p-3 mb-4 text-xs grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1" data-testid="moratorios-condiciones">
            <div className="sm:col-span-2 font-semibold text-text-primary" data-testid="moratorios-tasa">Condiciones del cálculo · Tasa moratoria de referencia: {rep.tasa_mensual}% mensual</div>
            <div><span className="text-text-muted">Base de cálculo:</span> saldo pendiente de cada factura</div>
            <div><span className="text-text-muted">Cálculo diario:</span> tasa mensual prorrateada sobre 30 días</div>
            <div className="sm:col-span-2 text-text-muted">Cargo moratorio = Saldo pendiente × {rep.tasa_mensual}% × (Días en mora / 30). Valores informativos a la fecha de cálculo, para gestión y conciliación de cobranza.</div>
          </div>
          {rep.facturas.length === 0 ? (
            <p className="text-sm text-text-secondary py-6 text-center" data-testid="moratorios-sin-vencidas">El cliente no tiene facturas vencidas con saldo a la fecha.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm min-w-[720px]">
                <thead className="text-left text-[10px] uppercase tracking-wider text-text-muted border-b border-border">
                  <tr><th className="p-2">Factura</th><th className="p-2">Emisión</th><th className="p-2">Vencimiento</th><th className="p-2 text-right">Días en mora</th>
                    <th className="p-2 text-right">Saldo pendiente</th><th className="p-2 text-right">Cargo moratorio</th><th className="p-2 text-right">Total con mora</th></tr>
                </thead>
                <tbody>
                  {rep.facturas.map((r) => (
                    <tr key={r.factura_id} className="border-b border-border/30" data-testid={`moratorio-${r.numero}`}>
                      <td className="p-2 font-mono text-xs">{r.numero}</td><td className="p-2">{fmtDate(r.fecha)}</td><td className="p-2">{fmtDate(r.vencimiento)}</td>
                      <td className="p-2 text-right">{r.dias}</td><td className="p-2 text-right tabular-nums">{fmtL(r.saldo)}</td>
                      <td className="p-2 text-right tabular-nums">{fmtL(r.cargo)}</td><td className="p-2 text-right tabular-nums font-semibold">{fmtL(r.total_con_mora)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="text-sm">
                  <tr><td colSpan={6} className="p-2 pt-3 text-right text-[10px] uppercase tracking-wider text-agro-green font-semibold">Resumen general</td><td /></tr>
                  <tr><td colSpan={6} className="p-2 text-right text-text-secondary">Total saldo pendiente</td><td className="p-2 text-right tabular-nums" data-testid="moratorios-total-saldo">{fmtL(t.saldo_vencido)}</td></tr>
                  <tr><td colSpan={6} className="p-2 text-right text-text-secondary">Total cargos moratorios</td><td className="p-2 text-right tabular-nums text-agro-yellow" data-testid="moratorios-total-cargos">{fmtL(t.cargos)}</td></tr>
                  <tr className="border-t-2 border-agro-green font-bold text-base"><td colSpan={6} className="p-2 text-right">TOTAL GENERAL CON MORA</td>
                    <td className="p-2 text-right tabular-nums text-agro-green" data-testid="moratorios-total-ref">{fmtL(t.total_referencia)}</td></tr>
                </tfoot>
              </table>
            </div>
          )}
          <p className="mt-4 text-[11px] text-text-muted flex gap-1.5"><InfoIcon size={14} className="shrink-0 mt-px" aria-hidden="true" /> {rep.nota}</p>
        </section>

        {preview && (
          <DocumentoPdfModal url={`/cobranza/cargos-moratorios/${rep.cliente.id}/pdf`} titulo="Reporte de cargos moratorios"
            numero={rep.cliente.nombre} archivo={`cargos-moratorios-${rep.cliente.codigo_cliente || "cliente"}-${rep.fecha}.pdf`}
            onClose={() => setPreview(false)} testid="moratorios-pdf" />
        )}
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="moratorios">
      <div className="bg-card border border-border/60 rounded-xl p-4 text-sm">
        <h3 className="font-heading text-base font-semibold flex items-center gap-2"><CalculatorIcon size={18} className="text-agro-green" /> Cargos moratorios</h3>
        <p className="text-xs text-text-secondary mt-1">
          Cálculo informativo de mora sobre facturas vencidas: saldo pendiente × {lista?.tasa_mensual ?? 2} % mensual × días de mora / 30.
          No genera asientos, notas ni cargos, y no modifica facturas, pagos ni saldos.
        </p>
      </div>
      <SearchBox busqueda={busqueda} placeholder="Buscar cliente por nombre, código o RTN" testid="moratorios-buscar" label="Buscar cliente con facturas vencidas" />
      {!lista ? <div className="text-sm text-text-muted">Cargando…</div>
        : busqueda.resultados.length === 0 ? (
          <div className="bg-card border border-border/60 rounded-xl p-8 text-center text-sm text-text-secondary" data-testid="moratorios-vacio">
            {busqueda.activa ? `Sin clientes que coincidan con «${busqueda.texto}».` : "No hay clientes con facturas vencidas."}</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {busqueda.resultados.map((c) => (
              <div key={c.id} className="bg-card border border-border/60 rounded-xl p-3 flex items-center gap-3" data-testid={`moratorios-cliente-${c.id}`}>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold truncate">{c.nombre}</div>
                  <div className="text-[11px] text-text-muted">{c.codigo_cliente || "—"} · {c.facturas} {c.facturas === 1 ? "factura vencida" : "facturas vencidas"} · saldo {fmtL(c.saldo)}</div>
                </div>
                <button type="button" onClick={() => generar(c)} disabled={Boolean(generando)} data-testid={`moratorios-generar-${c.id}`}
                  className="min-h-[44px] px-3 bg-agro-green/15 border border-agro-green/50 text-agro-green rounded-md text-xs font-semibold flex items-center gap-1.5 disabled:opacity-50">
                  <CalculatorIcon size={14} /> {generando === c.id ? "Calculando…" : "Generar reporte"}</button>
              </div>
            ))}
          </div>
        )}
    </div>
  );
}
